import { useState } from 'react';
import { ArrowLeft, Settings } from 'lucide-react';
import ReportSubNav from './ReportSubNav';
import PreferencesModal from './PreferencesModal';
import { formatPeriod } from './utils';

/**
 * Layout immersif du rapport cabinet — full-width, en rupture délibérée
 * avec la nav latérale Benchory du portail. Le pharmacien quitte l'app
 * et entre dans son rapport mensuel.
 *
 * Composé de :
 *   - Top bar : retour portail · branding Cabinet Benchory · ref édition
 *   - Sub-nav : 6 sous-onglets (sticky)
 *   - Main : contenu, max-width 5xl pour lisibilité
 *   - Footer : signature cabinet
 */
export default function ReportLayout({
  children, period, codeOfficine, currentSection,
  establishmentName, onSectionChange, onExit,
}) {
  const [prefsOpen, setPrefsOpen] = useState(false);

  return (
    <div className="min-h-screen bg-cabinet-cream font-manrope text-cabinet-ink antialiased" data-testid="report-layout">
      <nav className="border-b border-cabinet-border bg-cabinet-cream/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-8 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <button
              onClick={onExit}
              data-testid="report-exit-btn"
              className="flex items-center gap-1.5 text-[11px] text-cabinet-ink-secondary hover:text-cabinet-bordeaux uppercase tracking-[0.18em] transition"
            >
              <ArrowLeft size={13} /> Retour au portail
            </button>
            <div className="h-5 w-px bg-cabinet-border" />
            <div className="flex flex-col">
              <span className="font-playfair text-[16px] text-cabinet-bordeaux tracking-wide leading-tight">Cabinet Benchory</span>
              <span className="text-[9px] text-cabinet-ink-secondary uppercase tracking-[0.22em] mt-0.5">Pilotage officinal · Conseil stratégique</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[10px] text-cabinet-ink-secondary uppercase tracking-[0.2em]">
                Édition {formatPeriod(period)}
              </div>
              {codeOfficine && (
                <div className="text-[10px] text-cabinet-ink-soft uppercase tracking-[0.18em] mt-0.5">
                  Réf. {codeOfficine}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setPrefsOpen(true)}
              data-testid="report-preferences-btn"
              title="Préférences de salutation"
              className="p-2 rounded-full text-cabinet-ink-secondary hover:text-cabinet-bordeaux hover:bg-cabinet-cream-dark transition"
            >
              <Settings size={14} />
            </button>
          </div>
        </div>
      </nav>

      <PreferencesModal open={prefsOpen} onClose={() => setPrefsOpen(false)} />

      <ReportSubNav currentSection={currentSection} onChange={onSectionChange} />

      <main className="max-w-5xl mx-auto px-8 py-12" data-testid="report-content">
        {children}
      </main>

      <footer className="mt-20 border-t border-cabinet-border bg-cabinet-cream-dark py-10">
        <div className="max-w-5xl mx-auto px-8 text-center">
          <div className="font-playfair text-[14px] text-cabinet-bordeaux tracking-wide mb-1">Cabinet Benchory</div>
          <div className="text-[9px] text-cabinet-ink-soft uppercase tracking-[0.25em]">
            Pilotage officinal et conseil stratégique{establishmentName ? ` · ${establishmentName}` : ''}
          </div>
        </div>
      </footer>
    </div>
  );
}
